import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cs from "classnames";
import styles from "./styles.module.scss";
import { ChevronDownIcon } from "../NewOrderSelectApp/icons";
import { SettingsProfileFormProps, TimezoneOption } from "./types";

// Menu height cap (keep in sync with .menu max-height) — used to decide
// whether the list still fits under the trigger.
const MENU_MAX_HEIGHT = 280;
const MENU_GAP = 4;

/**
 * DS single select for the timezone: a control-styled trigger and an option
 * list portaled to <body> and pinned to the trigger (same pattern as the time
 * menu in NewOrderDailyLimits), so the settings card can't clip it.
 */
const TimezoneSelect: React.FC<{
  value: string;
  options: TimezoneOption[];
  onChange: (value: string) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState<React.CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const selected = options.find((tz) => tz.value === value);

  const placeMenu = useCallback(() => {
    const trigger = rootRef.current?.getBoundingClientRect();
    if (!trigger) return;
    const spaceBelow = window.innerHeight - trigger.bottom;
    const openUp =
      spaceBelow < MENU_MAX_HEIGHT + MENU_GAP && trigger.top > spaceBelow;
    setMenuBox({
      left: trigger.left,
      width: trigger.width,
      ...(openUp
        ? {
            bottom: window.innerHeight - trigger.top + MENU_GAP,
            maxHeight: Math.min(MENU_MAX_HEIGHT, trigger.top - MENU_GAP * 2),
          }
        : {
            top: trigger.bottom + MENU_GAP,
            maxHeight: Math.min(MENU_MAX_HEIGHT, spaceBelow - MENU_GAP * 2),
          }),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    placeMenu();
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    // A scroll anywhere moves the trigger — follow it. The menu's own list
    // scroll doesn't (and the capture listener sees it too), so skip it.
    const handleScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      placeMenu();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", placeMenu);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", placeMenu);
    };
  }, [open, placeMenu]);

  // Bring the current value into view once the menu is first placed. Not on
  // every reposition, or the list snaps back to the value while scrolling.
  const placed = menuBox !== null;
  useEffect(() => {
    if (!open || !placed) return;
    menuRef.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [open, placed]);

  return (
    <div ref={rootRef}>
      <button
        type="button"
        className={cs(styles.control, styles.selectTrigger, {
          [styles.selectTriggerOpen]: open,
        })}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.selectValue}>{selected?.label ?? value}</span>
        <ChevronDownIcon
          className={cs(styles.selectChevron, {
            [styles.selectChevronOpen]: open,
          })}
        />
      </button>
      {open &&
        menuBox &&
        createPortal(
          <ul
            ref={menuRef}
            className={styles.menu}
            style={menuBox}
            role="listbox"
          >
            {options.map((tz) => (
              <li key={tz.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={tz.value === value}
                  className={cs(styles.option, {
                    [styles.optionActive]: tz.value === value,
                  })}
                  onClick={() => {
                    onChange(tz.value);
                    setOpen(false);
                  }}
                >
                  {tz.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
};

/** Settings -> Profile: a pure form with the name, the email, the contacts and the password change. */
export const SettingsProfileForm: React.FC<SettingsProfileFormProps> = ({
  name,
  onNameChange,
  email,
  onEmailChange,
  organization = "",
  onOrganizationChange,
  phone = "",
  onPhoneChange,
  telegram = "",
  onTelegramChange,
  passwordSetOn,
  currentPassword,
  onCurrentPasswordChange,
  newPassword,
  onNewPasswordChange,
  onForgotPassword,
  timezone,
  timezoneOptions,
  onTimezoneChange,
  errors = {},
  dirty = false,
  saving = false,
  onCancel,
  onSave,
  className,
}) => {
  const disabled = !dirty || saving;

  return (
    <form
      className={cs(styles.form, className)}
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled) onSave();
      }}
    >
      <div className={styles.column}>
        <section className={styles.section}>
          <h2 className={styles.heading}>Personal info</h2>

          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input
              className={cs(styles.control, {
                [styles.controlError]: Boolean(errors.name),
              })}
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
            />
            {errors.name && <span className={styles.error}>{errors.name}</span>}
          </label>

          <label className={styles.field}>
            <span className={styles.label}>Email</span>
            <input
              className={cs(styles.control, {
                [styles.controlError]: Boolean(errors.email),
              })}
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
            />
            {errors.email && (
              <span className={styles.error}>{errors.email}</span>
            )}
          </label>

          {onOrganizationChange && (
            <label className={styles.field}>
              <span className={styles.label}>Organization</span>
              <input
                className={styles.control}
                placeholder="Organization"
                value={organization}
                onChange={(e) => onOrganizationChange(e.target.value)}
              />
            </label>
          )}

          {onPhoneChange && (
            <label className={styles.field}>
              <span className={styles.label}>Phone</span>
              <input
                className={cs(styles.control, {
                  [styles.controlError]: Boolean(errors.phone),
                })}
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phone}
                onChange={(e) => onPhoneChange(e.target.value)}
              />
              {errors.phone && (
                <span className={styles.error}>{errors.phone}</span>
              )}
            </label>
          )}

          {onTelegramChange && (
            <label className={styles.field}>
              <span className={styles.label}>Telegram</span>
              <input
                className={styles.control}
                placeholder="@username"
                value={telegram}
                onChange={(e) => onTelegramChange(e.target.value)}
              />
            </label>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Password</h2>
          {passwordSetOn && (
            <p className={styles.subheading}>Password set on {passwordSetOn}</p>
          )}

          <label className={styles.field}>
            <span className={styles.label}>Current password</span>
            <input
              className={cs(styles.control, {
                [styles.controlError]: Boolean(errors.currentPassword),
              })}
              type="password"
              autoComplete="current-password"
              placeholder="Enter current password"
              value={currentPassword}
              onChange={(e) => onCurrentPasswordChange(e.target.value)}
            />
            {errors.currentPassword && (
              <span className={styles.error}>{errors.currentPassword}</span>
            )}
          </label>

          <label className={cs(styles.field, styles.fieldTight)}>
            <span className={styles.label}>New password</span>
            <input
              className={cs(styles.control, {
                [styles.controlError]: Boolean(errors.newPassword),
              })}
              type="password"
              autoComplete="new-password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => onNewPasswordChange(e.target.value)}
            />
            {errors.newPassword && (
              <span className={styles.error}>{errors.newPassword}</span>
            )}
          </label>

          <button
            type="button"
            className={styles.link}
            onClick={onForgotPassword}
          >
            Forgot current password?
          </button>
        </section>

        <section className={styles.section}>
          <h2 className={styles.heading}>Timezone</h2>

          <div className={cs(styles.field, styles.fieldSnug)}>
            <span className={styles.label}>Timezone</span>
            <TimezoneSelect
              value={timezone}
              options={timezoneOptions}
              onChange={onTimezoneChange}
            />
          </div>
        </section>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={cs(styles.button, styles.buttonGhost)}
          disabled={disabled}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={cs(styles.button, styles.buttonPrimary)}
          disabled={disabled}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
};

export default SettingsProfileForm;
